import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Toggle } from '@prio365/prio365-react-library';
import UserAvatar from './UserAvatar';
import Flex from './Flex';
import { useDispatch, useSelector } from 'react-redux';
import { RootReducerState, getUserMe } from '../apps/main/rootReducer';
import useDebounce from '../hooks/useDebounce';
import classNames from 'classnames';
import { clearPrioCache } from '../actions';
import { loggedOut } from '../modules/auth/actions';
import { logoutWithAccount } from '../store/authEffect';
import { useTheme } from 'react-jss';
import { PrioTheme } from '../theme/types';
import { makePrioStyles } from '../theme/utils';
import LanguageSelector from '../modules/profile/components/LanguageSelector';
import { Tooltip } from 'antd';
import { ESettingScope, Setting, UpdateSetting } from '../models/Settings';
import {
  createUserSetting,
  updateUserSetting,
} from '../modules/settings/actions/setting';
import { createSelector } from 'reselect';
import { SettingState } from '../modules/settings/reducers/setting';

const useStyles = makePrioStyles((theme) => ({
  root: {
    zIndex: 500,
    right: 0,
    top: 48,
    width: 320,
    position: 'absolute',
    borderRadius: 2,
    backgroundColor: theme.colors.application.background.default,
    boxShadow: '0 24px 54px rgb(0 0 0 / 15%), 0 4.5px 13.5px rgb(0 0 0 / 8%)',
    marginLeft: '0!important',
    transition: 'all 0.2s',
    opacity: 0,
    color: theme.colors.application.typography.default,
    padding: theme.spacing.regular,
    visibility: 'hidden',
  },
  rootOpen: {
    opacity: 1,
    zIndex: 999,
    visibility: 'visible',
  },
  info: {
    overflow: 'hidden',
  },
  avatar: {
    cursor: 'pointer',
    marginRight: theme.spacing.small,
  },
  headLine: {
    color: theme.colors.application.typography.default,
    fontSize: '18px',
    fontWeight: theme.font.fontWeight.bold,
  },
  email: {
    fontSize: theme.font.fontSize.small,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
}));

const betaSettingsSelector = createSelector<
  [(state: RootReducerState) => SettingState],
  Setting[]
>(
  (state) => state.settings.setting,
  ({ settings }) => {
    return settings.filter(
      (setting) =>
        setting.settingId !== '00000000-0000-0000-0000-000000000000' &&
        setting.key === 'General_BetaFeaturesEnabled'
    );
  }
);

export const AccountInformationPanel: React.FC = () => {
  //#region -------------------------------- Variables
  const classes = useStyles();
  const { t } = useTranslation();
  const ref = useRef(null);
  const buttonRef = useRef(null);
  const dispatch = useDispatch();
  const theme = useTheme<PrioTheme>();

  const [visible, setVisible] = useState<boolean>();
  const debouncedVisible = useDebounce(visible, 200);

  const userMe = useSelector(getUserMe);
  const betaSettings = useSelector(betaSettingsSelector);

  const betaUserSettings = betaSettings.filter(
    (setting) => setting.scope === ESettingScope.USER
  );

  const isBetaFeaturesEnabled = useMemo(() => {
    const defaultSetting =
      betaUserSettings.find((setting) => setting.isDefault === true) ||
      betaUserSettings[0] ||
      betaSettings[0];

    const value = JSON.parse(defaultSetting?.jsonValue || '{}');
    return value?.isEnabled ?? false;
  }, [betaSettings, betaUserSettings]);
  //#endregion

  //#region -------------------------------- Methods
  const handleEnableBetaChange = useCallback(
    (value: boolean) => {
      const updatedSettings = {
        jsonValue: JSON.stringify({
          isEnabled: value,
        }),
        parentSettingId: '',
        relationId: '',
        isDefault: true,
        description: '',
        scope: ESettingScope.USER,
        scopeId: userMe.id,
      } as UpdateSetting;

      if (betaUserSettings?.length === 0) {
        dispatch(
          createUserSetting(
            updatedSettings,
            userMe.id,
            'General_BetaFeaturesEnabled'
          )
        );
      } else {
        dispatch(updateUserSetting(updatedSettings, betaSettings[0].settingId));
      }
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    },
    [betaSettings, betaUserSettings, dispatch, userMe]
  );

  const signOut = () => {
    dispatch(clearPrioCache());
    dispatch(loggedOut());
    logoutWithAccount();
  };
  //#endregion

  //#region ------------------------------ Effects
  //#endregion

  return (
    <div
      tabIndex={-1}
      onBlur={(event) => {
        if (
          event.relatedTarget !== buttonRef.current &&
          event.relatedTarget?.id !== 'languageSelector' &&
          event.relatedTarget?.id !== 'beta-toggle'
        ) {
          setVisible((prev) => {
            if (prev) {
              return false;
            }
            return prev;
          });
        } else if (
          event.relatedTarget?.id === 'beta-toggle' ||
          event.relatedTarget?.id === 'languageSelector'
        ) {
          ref.current?.focus();
        }
      }}
      ref={ref}
    >
      <div
        onClick={() => {
          setVisible(!visible);
        }}
        className={classes.avatar}
      >
        <UserAvatar size="medium" showMe />
      </div>
      <div
        className={classNames(classes.root, {
          [classes.rootOpen]: visible,
        })}
      >
        {(visible || debouncedVisible) && (
          <Flex.Column childrenGap={theme.spacing.regular}>
            <Flex.Row
              justifyContent="space-between"
              childrenGap={theme.spacing.regular}
            >
              <Flex.Item>
                <UserAvatar size="large" showMe />
              </Flex.Item>
              <Flex.Column
                className={classes.info}
                flex={1}
                childrenGap={theme.spacing.regular}
              >
                <Flex.Column>
                  <Flex.Item>
                    <div className={classes.headLine}>
                      {userMe?.displayName ?? ''}
                    </div>
                  </Flex.Item>
                  <Flex.Item>
                    <Tooltip zIndex={999999} title={userMe?.mail ?? ''}>
                      <div className={classes.email}>{userMe?.mail ?? ''}</div>
                    </Tooltip>
                  </Flex.Item>
                </Flex.Column>
              </Flex.Column>
            </Flex.Row>

            <Flex.Item>
              <div
                style={{
                  fontSize: theme.font.fontSize.extraSmall,
                  color: theme.colors.application.typography.muted,
                }}
              >
                {t('common:navigationBar.language')}
              </div>
              <LanguageSelector />
            </Flex.Item>
            <Flex.Item>
              <div
                style={{
                  fontSize: theme.font.fontSize.extraSmall,
                  color: theme.colors.application.typography.muted,
                }}
              >
                {t('common:navigationBar.betaEnabled')}
              </div>
              <div onClick={(e) => e.stopPropagation()}>
                <Toggle
                  id="beta-toggle"
                  checked={isBetaFeaturesEnabled}
                  onChange={(value, event) => {
                    event.stopPropagation();
                    handleEnableBetaChange(value);
                  }}
                />
              </div>
            </Flex.Item>
            <Flex.Row justifyContent="flex-end">
              <Flex.Item>
                <Tooltip
                  title={t('common:navigationBar.signOut')}
                  placement="bottom"
                >
                  <div>
                    <Button
                      onClick={signOut}
                      type="primary"
                      ref={buttonRef}
                      suffixIconProp={['fal', 'sign-out']}
                    >
                      <>{t('common:navigationBar.signOut')}</>
                    </Button>
                  </div>
                </Tooltip>
              </Flex.Item>
            </Flex.Row>
          </Flex.Column>
        )}
      </div>
    </div>
  );
};

export default AccountInformationPanel;
